import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AppSettings } from 'pac-api';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { MembershipSelectionService } from './association-level/membership-selection.service';
import { CatchError, empty } from './helpers';
import { LoggerService } from './logger.service';
import { Association } from './model/assoication';
import { OrganizationModel } from './model/organization.model';
import { ITrainingRecord, TrainingRecord } from './model/training-record';
import { WizardMembershipSelection } from './model/wizard.model';
import { GenericErrorHandlerService } from './shared/generic-error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class AccountTrainingService {

  public Training: Map<Association, TrainingRecord>;

  public Exempt: Map<Association, string[]> = new Map<Association, string[]>([
    ['ABMP', ['KS', 'MN', 'WY']],
    ['ASCP', []],
    ['AHP', []],
    ['ANP', []],
  ]);

  private cache: Map<string, TrainingRecord>;

  constructor(
    @Inject(PLATFORM_ID) platformId: any,
    private membership: MembershipSelectionService,
    private genericErrorHandler: GenericErrorHandlerService,
    private http: HttpClient,
    private logger: LoggerService,
    private settings: AppSettings,
  ) {
    this.Training = new Map<Association, TrainingRecord>();
    this.cache = null;
  }

  public GetTrainingRecords(): Observable<TrainingRecord[]> {
    if (this.cache) {
      return of(Array.from(this.cache.values()));
    }

    const observable = this.http.get(
      this.settings.pac_base_nf_url + 'api/PACMembershipTrainingDetail',
      {
        params: {},
        withCredentials: true
      }
    );

    return observable.pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if (result instanceof Array) {
        this.cache = new Map<string, TrainingRecord>();
        const records = Array<TrainingRecord>();
        for (const record of result) {
          const training = new TrainingRecord(record);
          records.push(training);
          this.cache.set(training.Key, training);
        }
        return records;
      }
    }), tap(result => {
      try {
        this.logger.info(result);
      } catch {
        // IE is a big baby
      }
    }, this.genericErrorHandler.Hanlder));
  }

  public GetTrainingRecord(asn: Association, skeleton: ITrainingRecord = null): Observable<TrainingRecord> {
    if (empty(asn)) {
      throw new Error('asn must be specified');
    }

    return this.GetTrainingRecords().pipe(map(results => {
      const matching: TrainingRecord[] = results.filter(record => {
        let match = record.Association === asn;

        if (match) {
          if (skeleton) {
            if (record.School.Key && skeleton.School.Key) {
              match = record.School.Key.toLowerCase() == skeleton.School.Key.toLowerCase();
            }
          }
        }

        return match;
      }).sort((a, b) => {
        return a.Added.getTime() - b.Added.getTime();
      });

      if (matching.length === 1) {
        return matching[0];
      } else if (matching.length > 1) {
        const primary = matching.find(record => record.Primary);
        if (primary) {
          return primary;
        }
        return matching[0];
      }

      return null;
    }), switchMap(record => {
      if (record) {
        return of(record);
      }

      const training = new TrainingRecord(skeleton);
      training.Association = asn;
      training.Primary = true;
      return this.Save(training).pipe(tap(created => {
        if (this.cache) {
          this.cache.set(created.Key, created);
        }
      }));
    }), tap(record => {
      if (record instanceof TrainingRecord) {
        this.Training.set(record.Association, record);
      }
    }, this.genericErrorHandler.Hanlder));
  }

  public Save(record: ITrainingRecord): Observable<TrainingRecord> {

    let observable: Observable<any>;

    if (empty(record.Key)) {
      observable = this.http.post(this.settings.pac_base_nf_url + 'api/PACMembershipTrainingDetail', record, {
        withCredentials: true
      });
    } else {
      observable = this.http.put(this.settings.pac_base_nf_url + 'api/PACMembershipTrainingDetail/' + record.Key, record, {
        withCredentials: true
      });
    }

    return observable.pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if (result.Key) {
        const training = new TrainingRecord(result);
        this.Training.set(training.Association, training);
        if (this.cache) {
          this.cache.set(training.Key, training);
        }
        return training;
      }
    }), tap(result => {
      try {
        this.logger.info(result);
      } catch {
        // IE is a big baby
      }
    }, this.genericErrorHandler.Hanlder));
  }

  public Schools(state: string, query: string): Observable<OrganizationModel> {
    const request = this.http.get<any>(
      this.settings.pac_base_nf_url + 'api/School/' + state + '?query=' + encodeURIComponent(query),
      {
        withCredentials: true
      }
    ).pipe(map(result => {
      if (result.error) {
        return result;
      } else if (result instanceof Array) {
        return result.map(org => new OrganizationModel(org));
      }
    }, this.genericErrorHandler.Hanlder));

    return request;
  }

  public Instructors(key: string, query: string): Observable<any> {
    return this.http.get<any>(
      this.settings.pac_base_nf_url + 'api/Instructor/' + key + '?query=' + encodeURIComponent(query),
      {
        withCredentials: true
      }
    ).pipe(map(result => {
      if (result.error) {
        return [];
      } else if (result instanceof Array) {
        return result;
      }
    }, this.genericErrorHandler.Hanlder));
  }

  public GetMembershipSelection(asn: Association): WizardMembershipSelection {
    if (this.membership.Data.Associations[asn]) {
      return this.membership.Data.Memberships[asn];
    }

    return null;
  }
}
